package com.checkin.host.ui.billing

import androidx.compose.runtime.*
import com.checkin.host.api.ApiClient
import com.checkin.host.api.ApiException
import com.checkin.host.theme.AppColors
import com.checkin.host.theme.cardStyle
import com.checkin.host.theme.primaryButton
import com.checkin.host.theme.secondaryButton
import com.checkin.host.ui.layout.AlertBanner
import com.checkin.host.ui.layout.PageHeader
import com.checkin.host.ui.layout.rememberIsMobile
import com.checkin.shared.dto.SubscriptionDto
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

private data class PlanOffer(
    val id: String,
    val name: String,
    val priceLabel: String,
    val propertiesLabel: String,
    val checkinsLabel: String,
    val popular: Boolean = false,
)

private val PLAN_OFFERS = listOf(
    PlanOffer("SOLO", "Solo", "89 MAD / mois", "1 bien", "Check-ins illimités"),
    PlanOffer("CONCIERGERIE", "Conciergerie", "179 MAD / mois", "3 biens", "Check-ins illimités"),
    PlanOffer("PRO", "Pro", "249 MAD / mois", "5 biens", "Check-ins illimités", popular = true),
    PlanOffer("AGENCE", "Agence", "349 MAD / mois", "10 biens", "Check-ins illimités"),
    PlanOffer("UNLIMITED", "Illimité", "449 MAD / mois", "Biens illimités", "Check-ins illimités"),
)

@Composable
fun SubscriptionScreen(
    checkoutReturn: BillingCheckoutReturn? = null,
    onCheckoutReturnConsumed: () -> Unit = {},
) {
    var subscription by remember { mutableStateOf<SubscriptionDto?>(null) }
    var loading by remember { mutableStateOf(true) }
    var checkoutPlan by remember { mutableStateOf<String?>(null) }
    var portalLoading by remember { mutableStateOf(false) }
    var message by remember { mutableStateOf<String?>(null) }
    var isError by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()
    val isMobile = rememberIsMobile()

    fun loadSubscription() {
        scope.launch {
            loading = true
            message = null
            try {
                subscription = ApiClient.getSubscription()
                isError = false
            } catch (e: ApiException) {
                message = e.message
                isError = true
            } finally {
                loading = false
            }
        }
    }

    LaunchedEffect(Unit) { loadSubscription() }

    LaunchedEffect(checkoutReturn) {
        checkoutReturn ?: return@LaunchedEffect
        loadSubscription()
        val planName = checkoutReturn.planId?.let { id ->
            PLAN_OFFERS.firstOrNull { it.id.equals(id, ignoreCase = true) }?.name ?: id
        }
        message = if (planName != null) {
            "Paiement réussi ! Votre abonnement $planName est en cours d'activation."
        } else {
            "Paiement réussi ! Votre abonnement est en cours d'activation."
        }
        isError = false
        onCheckoutReturnConsumed()
    }

    PageHeader("Abonnement", "Gérez votre plan et les limites de check-in invité")

    message?.let { AlertBanner(it, isError) }

    if (loading && subscription == null) {
        P({ style { color(AppColors.muted) } }) { Text("Chargement…") }
        return
    }

    val sub = subscription
    if (sub != null) {
        CurrentPlanCard(
            sub = sub,
            portalLoading = portalLoading,
            onManageSubscription = {
                scope.launch {
                    portalLoading = true
                    message = null
                    try {
                        val response = ApiClient.getCustomerPortalUrl()
                        window.location.href = response.url
                    } catch (e: ApiException) {
                        message = e.message
                        isError = true
                        portalLoading = false
                    }
                }
            },
        )
    }

    if (sub != null && sub.propertiesOverLimit > 0) {
        DowngradeOverLimitBanner(sub)
    }

    if (sub != null && !sub.checkoutAvailable) {
        Div({
            style {
                cardStyle()
                marginBottom(20.px)
                backgroundColor(Color(AppColors.warningBg))
                border(1.px, LineStyle.Solid, Color(AppColors.warningBorder))
            }
        }) {
            P({
                style {
                    margin(0.px)
                    color(AppColors.warning)
                    fontSize(14.px)
                    lineHeight("1.5")
                }
            }) {
                Text(
                    "Paiement en ligne non configuré sur le serveur (clés Lemon Squeezy ou variant IDs manquants). " +
                        "Contactez l'administrateur ou vérifiez les variables Railway.",
                )
            }
        }
    }

    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.Center)
            flexWrap(FlexWrap.Wrap)
            gap(12.px)
            marginBottom(16.px)
        }
    }) {
        H3({ style { margin(0.px); fontSize(18.px); color(AppColors.text) } }) { Text("Plans disponibles") }
        Button({
            style { secondaryButton() }
            onClick { loadSubscription() }
        }) { Text(if (loading) "…" else "Actualiser") }
    }

    if (sub != null && sub.checkoutAvailable) {
        P({
            style {
                color(AppColors.muted)
                fontSize(13.px)
                marginBottom(16.px)
                lineHeight("1.5")
            }
        }) {
            Text("Mode test : carte 4242 4242 4242 4242. Après paiement, vous serez redirigé ici automatiquement.")
        }
    }

    Div({
        style {
            display(DisplayStyle.Grid)
            property(
                "grid-template-columns",
                if (isMobile) "1fr" else "repeat(auto-fill, minmax(220px, 1fr))",
            )
            gap(16.px)
        }
    }) {
        PLAN_OFFERS.forEach { offer ->
            val isCurrent = sub?.plan?.equals(offer.id, ignoreCase = true) == true &&
                sub.status == "active"
            val canCheckout = sub?.checkoutAvailable == true && !isCurrent

            PlanCard(
                offer = offer,
                isCurrent = isCurrent,
                loading = checkoutPlan == offer.id,
                enabled = canCheckout,
                onSelect = {
                    scope.launch {
                        checkoutPlan = offer.id
                        message = null
                        try {
                            val response = ApiClient.createCheckout(offer.id)
                            window.location.href = response.url
                        } catch (e: ApiException) {
                            message = e.message
                            isError = true
                        } finally {
                            checkoutPlan = null
                        }
                    }
                },
            )
        }
    }
}

@Composable
private fun DowngradeOverLimitBanner(sub: SubscriptionDto) {
    val deadlineText = sub.planDowngradeDeadline?.substringBefore('T')?.let { iso ->
        val parts = iso.split('-')
        if (parts.size == 3) "${parts[2]}/${parts[1]}/${parts[0]}" else iso
    }
    Div({
        style {
            cardStyle()
            marginBottom(20.px)
            backgroundColor(Color(AppColors.warningBg))
            border(1.px, LineStyle.Solid, Color(AppColors.warningBorder))
        }
    }) {
        P({
            style {
                margin(0.px)
                color(AppColors.warning)
                fontSize(14.px)
                lineHeight("1.6")
            }
        }) {
            Text(
                buildString {
                    append("Votre plan autorise ${sub.propertiesLimit} bien(s) actif(s) ; vous en avez ${sub.propertiesUsed}. ")
                    append("Archivez ${sub.propertiesOverLimit} hébergement(s) au choix dans Hébergements")
                    deadlineText?.let { append(" avant le $it") }
                    append(" — sinon les plus récents seront archivés automatiquement.")
                },
            )
        }
    }
}

@Composable
private fun CurrentPlanCard(
    sub: SubscriptionDto,
    portalLoading: Boolean,
    onManageSubscription: () -> Unit,
) {
    val planName = PLAN_OFFERS.firstOrNull { it.id.equals(sub.plan, ignoreCase = true) }?.name
        ?: if (sub.plan.equals("FREE", ignoreCase = true)) "Gratuit" else sub.plan
    val propertiesLimitLabel = if (sub.propertiesLimit >= Int.MAX_VALUE / 2) {
        "Illimité"
    } else {
        "${sub.propertiesUsed} / ${sub.propertiesLimit}"
    }

    Div({
        style {
            cardStyle()
            marginBottom(20.px)
            property("background", AppColors.planCardGradient)
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(10.px)
                marginBottom(16.px)
            }
        }) {
            Div({
                style {
                    width(8.px)
                    height(8.px)
                    borderRadius(50.percent)
                    backgroundColor(if (sub.status == "active") AppColors.success else AppColors.muted)
                    property("box-shadow", "0 0 8px ${if (sub.status == "active") AppColors.success else "transparent"}")
                }
            })
            H3({ style { margin(0.px); fontSize(16.px); color(AppColors.text) } }) { Text("Votre plan actuel") }
        }

        Div({
            style {
                display(DisplayStyle.Grid)
                property("grid-template-columns", "repeat(auto-fit, minmax(140px, 1fr))")
                gap(16.px)
                marginBottom(if (sub.canManageSubscription) 16.px else 0.px)
            }
        }) {
            StatBlock("Plan", planName)
            StatBlock("Statut", statusLabel(sub.status))
            StatBlock("Biens", propertiesLimitLabel)
            StatBlock(
                "Check-ins essai",
                if (sub.plan.equals("FREE", ignoreCase = true) || sub.status != "active") {
                    "${sub.freeCheckinsRemaining} restants"
                } else {
                    "Illimités"
                },
            )
        }
        if (sub.canManageSubscription) {
            Div({
                style {
                    paddingTop(16.px)
                    property("border-top", "1px solid ${AppColors.border}")
                }
            }) {
                P({
                    style {
                        margin(0.px, 0.px, 10.px, 0.px)
                        color(AppColors.muted)
                        fontSize(13.px)
                        lineHeight("1.5")
                    }
                }) {
                    Text(
                        "Pause, annulation, changement de carte ou reprise d'abonnement via le portail sécurisé Lemon Squeezy.",
                    )
                }
                Button({
                    style {
                        secondaryButton()
                        if (portalLoading) { property("opacity", "0.55"); cursor("wait") }
                    }
                    if (!portalLoading) onClick { onManageSubscription() }
                }) { Text(if (portalLoading) "Ouverture…" else "Gérer mon abonnement") }
            }
        }
    }
}

@Composable
private fun StatBlock(label: String, value: String) {
    Div {
        Div({ style { fontSize(11.px); color(AppColors.muted); marginBottom(4.px); property("text-transform", "uppercase"); letterSpacing(0.5.px); fontWeight("600") } }) { Text(label) }
        Div({ style { fontSize(16.px); fontWeight("700"); color(AppColors.text) } }) { Text(value) }
    }
}

@Composable
private fun PlanCard(
    offer: PlanOffer,
    isCurrent: Boolean,
    loading: Boolean,
    enabled: Boolean,
    onSelect: () -> Unit,
) {
    Div({
        style {
            cardStyle()
            position(Position.Relative)
            overflow("hidden")
            if (isCurrent) {
                border(2.px, LineStyle.Solid, AppColors.primary)
                property("box-shadow", "0 0 0 1px rgba(99,102,241,0.2), 0 4px 20px rgba(99,102,241,0.1)")
            } else if (offer.popular) {
                border(1.px, LineStyle.Solid, Color("rgba(99,102,241,0.4)"))
            }
        }
    }) {
        // Popular badge
        if (offer.popular && !isCurrent) {
            Div({
                style {
                    position(Position.Absolute)
                    property("top", "12px")
                    property("right", "12px")
                    fontSize(10.px)
                    fontWeight("700")
                    padding(3.px, 8.px)
                    borderRadius(20.px)
                    backgroundColor(Color(AppColors.primaryGlow))
                    color(AppColors.primary)
                    property("letter-spacing", "0.5px")
                    property("text-transform", "uppercase")
                }
            }) { Text("Populaire") }
        }
        // Current badge
        if (isCurrent) {
            Div({
                style {
                    position(Position.Absolute)
                    property("top", "12px")
                    property("right", "12px")
                    fontSize(10.px)
                    fontWeight("700")
                    padding(3.px, 8.px)
                    borderRadius(20.px)
                    backgroundColor(Color(AppColors.successBg))
                    color(AppColors.success)
                    property("text-transform", "uppercase")
                    property("letter-spacing", "0.5px")
                }
            }) { Text("Actuel") }
        }

        Div({ style { fontSize(17.px); fontWeight("700"); color(AppColors.text); marginBottom(4.px) } }) {
            Text(offer.name)
        }
        Div({
            style {
                fontSize(22.px)
                fontWeight("700")
                color(if (isCurrent) AppColors.primary else AppColors.text)
                marginBottom(16.px)
                letterSpacing((-0.5).px)
            }
        }) { Text(offer.priceLabel) }

        Div({ style { marginBottom(20.px) } }) {
            FeatureRow("⌂ ${offer.propertiesLabel}")
            FeatureRow("✓ ${offer.checkinsLabel}")
        }

        if (!isCurrent) {
            Button({
                style {
                    primaryButton()
                    width(100.percent)
                    if (!enabled || loading) {
                        property("opacity", "0.45")
                        cursor("not-allowed")
                    }
                    if (offer.popular && enabled) {
                        property("box-shadow", "0 0 16px rgba(99,102,241,0.35)")
                    }
                }
                if (enabled && !loading) onClick { onSelect() }
            }) {
                Text(
                    when {
                        loading -> "Ouverture…"
                        !enabled -> "Indisponible"
                        else -> "Choisir ce plan"
                    },
                )
            }
        }
    }
}

@Composable
private fun FeatureRow(text: String) {
    Div({
        style {
            fontSize(13.px)
            color(AppColors.muted)
            marginBottom(6.px)
            display(DisplayStyle.Flex)
            gap(6.px)
            alignItems(AlignItems.Center)
        }
    }) { Text(text) }
}

private fun statusLabel(status: String): String = when (status.lowercase()) {
    "active" -> "Actif"
    "on_trial", "trialing" -> "Essai"
    "cancelled" -> "Annulé"
    "past_due", "unpaid" -> "Paiement en retard"
    "expired" -> "Expiré"
    "free" -> "Gratuit"
    else -> status
}
